/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import {
  Editable,
  ReactEditor,
  Slate,
  withReact,
  DefaultElement,
  RenderElementProps,
} from 'slate-react';
import { withHistory } from 'slate-history';

import {
  createEditor,
  Editor,
  Node,
  SetNodeOperation,
  Transforms,
} from 'slate';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { IRichTextInput } from './rich-text.model';
import { useEffect } from 'react';
import { isArray, isEmpty, isNil, uniqBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { useLatest } from 'react-use';
import { setCanRedo, setCanUndo } from 'slices/transcript.slice';
import { TimecodeModal } from '../modals/TimecodeModal';
import {
  clipSearchRangeToLeaf,
  getSlateSelection,
  indexSearchRangesByRow,
  isPublicShared,
} from 'components/VideoPlayer/Transcription/MediaUtilities';
import {
  EDIT_MODE_OVERSCAN_PX,
  resolveTranscriptScrollRoot,
  transcriptVisibility,
  transcriptVisibilityKey,
  VIEW_MODE_OVERSCAN_PX,
} from 'components/VideoPlayer/Transcription/MediaPlayer/Transcript/transcriptVisibility';
import {
  flushPlaybackHighlight,
  flushPlaybackSeek,
  resetPlaybackHighlight,
  schedulePlaybackHighlight,
  shouldIgnoreVideoSeekEvent,
  setPlaybackHighlightCaptionHandler,
  setPlaybackHighlightEditModeGetter,
  setPlaybackHighlightFollowingGetter,
  startPlaybackHighlightLoop,
  stopPlaybackHighlightLoop,
  syncBootTranscriptPresentation,
  syncTranscriptPlaybackChrome,
} from 'components/VideoPlayer/Transcription/MediaPlayer/Transcript/playbackHighlight';
import { transcriptChunkStore } from 'components/VideoPlayer/Transcription/MediaPlayer/Transcript/TranscriptChunkStore';
import { Transcript } from 'components/VideoPlayer/Transcription/MediaPlayer/Transcript/Transcript';
import { Keys } from 'utils/enum';
import { breakCaption } from 'utils/caption.util';
import {
  setCurrentChapterTime,
  setSplitCaption,
  setSplitParagraph,
} from 'slices/chapter.slice';
import { CustomEditor, CustomElement } from 'types/slate-custom-types';
import { Layers } from 'utils/enum';
import { toggleEditCaption } from 'slices/toggle.slice';
import { IKeyItem } from 'utils/models';

declare global {
  interface Window {
    Editor: CustomEditor;
    Video: HTMLVideoElement;
  }
}

const commonStyles = css`
  & ::selection {
    ${tw`bg-human-selection!`}
  }
`;

interface IRichTextEditorProps {
  document: IRichTextInput[];
  videoTimePosition: number;
  setDocument: (transcript: CustomElement[]) => void;
  /** Block typing/selection while a chunk/window is loading after scroll. */
  interactionLocked?: boolean;
}

const TYPING_SLOW_LOG_MS = 0;

export const RichTextEditor = ({
  document,
  videoTimePosition: _videoTimePosition,
  setDocument,
  interactionLocked = false,
}: IRichTextEditorProps) => {
  // const [documentSrt, setDocumentSrt] = useState<Descendant[]>(document);

  // console.count('RICHTEXT')

  const editor = useMemo<CustomEditor>(
    () => withHistory(withReact(createEditor() as ReactEditor)),
    [],
  );
  // FOR DEV ONLY
  // const editorRef = useRef<any>();
  // if (!editorRef.current)
  //   editorRef.current = withHistory(withReact(createEditor() as ReactEditor));
  // const editor = editorRef.current;

  const dispatch = useDispatch();
  const shouldLogTypingPerf = useCallback(() => {
    try {
      return (
        (window as any)?.__TRANSCRIPT_TYPING_DEBUG__ === true ||
        window.localStorage?.getItem('transcriptTypingDebug') === '1'
      );
    } catch {
      return false;
    }
  }, []);
  const toggle = useSelector((state: RootState) => state.toggle);
  const [finderLayoutKey, setFinderLayoutKey] = useState('');
  useEffect(() => {
    let lastKey = '';
    const syncFinderLayout = () => {
      const nextKey = `${transcriptChunkStore.getActiveChunkId() ?? ''}:${
        transcriptChunkStore.getEditorReadyRevision()
      }:${transcriptChunkStore.isEditorReady()}`;
      if (nextKey === lastKey) return;
      lastKey = nextKey;
      setFinderLayoutKey(nextKey);
    };
    syncFinderLayout();
    return transcriptChunkStore.subscribe(syncFinderLayout);
  }, []);
  const isFollowingRef = useRef(toggle.isFollowing);
  isFollowingRef.current = toggle.isFollowing && !toggle.isEditMode;

  const startHighlightForVideo = useCallback((video: HTMLMediaElement) => {
    if (transcriptChunkStore.isDwcm()) return;
    startPlaybackHighlightLoop({ isFollowing: isFollowingRef.current });
  }, []);

  const renderElement = useCallback(
    (props: RenderElementProps) => (
      <Element
        {...props}
        isEditMode={toggle.isEditMode}
        handleChangeTimecode={handleChangeTimecode}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const canUndoStore = useSelector(
    (state: RootState) => state.transcript.canUndo,
  );
  const canRedoStore = useSelector(
    (state: RootState) => state.transcript.canRedo,
  );

  const timecodeRef = useRef<any>(null);
  const lastSelectionPinRef = useRef<string>('');
  const transcriptCanUndoRef = useRef(canUndoStore);
  const transcriptCanRedoRef = useRef(canRedoStore);
  transcriptCanUndoRef.current = canUndoStore;
  transcriptCanRedoRef.current = canRedoStore;

  const isEditModeRef = useRef(toggle.isEditMode);
  isEditModeRef.current = toggle.isEditMode;

  /**
   * Windowing policy (SPP-654):
   * Keep windowing ON always. Playback highlight is CSS/rAF (playbackHighlight.ts),
   * not Slate decorate — decorate-on-timeupdate froze large files.
   */
  const syncPlaybackWindowing = useCallback(
    (opts?: { resetVisibility?: boolean }) => {
      const root = resolveTranscriptScrollRoot();
      transcriptVisibility.setScrollRoot(root);
      transcriptVisibility.setEnabled(true);

      if (isEditModeRef.current) {
        transcriptVisibility.setOverscanPx(EDIT_MODE_OVERSCAN_PX);
        stopPlaybackHighlightLoop();
        const video = window.Video;
        if (video) {
          flushPlaybackHighlight(video.currentTime || 0, {
            isFollowing: false,
          });
        }
        return;
      }

      transcriptVisibility.setOverscanPx(VIEW_MODE_OVERSCAN_PX);
      if (opts?.resetVisibility) {
        transcriptVisibility.reset();
      }

      const video = window.Video;
      if (!video) return;

      flushPlaybackHighlight(video.currentTime || 0, {
        isFollowing: isFollowingRef.current,
      });

      if (!video.paused && !video.ended) {
        startHighlightForVideo(video);
      } else {
        stopPlaybackHighlightLoop();
      }
    },
    [startHighlightForVideo],
  );

  useEffect(() => {
    // Avoid setCurrentPath here — it re-renders every Transcript row on each caption.
    // Play-button chrome is CSS-driven in playbackHighlight.ts.
    setPlaybackHighlightCaptionHandler(({ captionStart }) => {
      dispatch(setCurrentChapterTime(captionStart));
    });
    setPlaybackHighlightEditModeGetter(() => isEditModeRef.current);
    setPlaybackHighlightFollowingGetter(
      () => isFollowingRef.current && !isEditModeRef.current,
    );
    return () => {
      setPlaybackHighlightCaptionHandler(null);
      setPlaybackHighlightEditModeGetter(null);
      setPlaybackHighlightFollowingGetter(null);
      stopPlaybackHighlightLoop();
    };
  }, [dispatch]);

  const prevEditModeRef = useRef<boolean | null>(null);

  useEffect(() => {
    // SPP-655: RichTextEditor remounts on chunk load/scroll-freeze. Do NOT
    // reset()/rebuildObserver on every remount — only when Edit→View actually toggles.
    const prev = prevEditModeRef.current;
    const next = toggle.isEditMode;
    const modeChanged = prev !== null && prev !== next;
    prevEditModeRef.current = next;

    syncPlaybackWindowing({
      resetVisibility: modeChanged && !next, // entering View from Edit only
    });
    lastSelectionPinRef.current = '';
    if (next) {
      stopPlaybackHighlightLoop();
      const video = window.Video;
      if (video) {
        flushPlaybackHighlight(video.currentTime || 0, { isFollowing: false });
      }
    }
  }, [toggle.isEditMode, syncPlaybackWindowing]);

  // window.Video is assigned after media ready — retry attach until present.
  useEffect(() => {
    let attached: HTMLMediaElement | null = null;
    let cancelled = false;

    const onPlay = () => {
      syncTranscriptPlaybackChrome();
      syncPlaybackWindowing();
      const video = window.Video;
      if (!video) return;
      if (isEditModeRef.current) {
        schedulePlaybackHighlight(video.currentTime || 0, {
          isFollowing: false,
        });
        if (!transcriptChunkStore.isDwcm()) {
          startPlaybackHighlightLoop({ isFollowing: false });
        }
        return;
      }
      startHighlightForVideo(video);
    };
    const onPauseOrEnd = () => {
      syncTranscriptPlaybackChrome();
      stopPlaybackHighlightLoop();
      syncPlaybackWindowing();
    };
    const onSeeking = () => {
      if (shouldIgnoreVideoSeekEvent()) return;
      const video = window.Video;
      if (!video) return;
      flushPlaybackSeek(video.currentTime || 0, {
        isFollowing: isFollowingRef.current,
      });
    };
    const onSeeked = () => {
      if (shouldIgnoreVideoSeekEvent()) return;
      const video = window.Video;
      if (!video) return;
      flushPlaybackSeek(video.currentTime || 0, {
        isFollowing: isFollowingRef.current,
      });
      if (!video.paused && !video.ended) {
        if (isEditModeRef.current) {
          schedulePlaybackHighlight(video.currentTime || 0, {
            isFollowing: false,
          });
          if (!transcriptChunkStore.isDwcm()) {
            startPlaybackHighlightLoop({ isFollowing: false });
          }
        } else {
          startHighlightForVideo(video);
        }
      }
    };
    const onTimeUpdate = () => {
      if (!transcriptChunkStore.isDwcm()) return;
      const video = window.Video;
      if (!video || video.paused || video.ended) return;
      schedulePlaybackHighlight(video.currentTime || 0, {
        isFollowing: isFollowingRef.current,
      });
    };

    const detach = () => {
      if (!attached) return;
      attached.removeEventListener('play', onPlay);
      attached.removeEventListener('playing', onPlay);
      attached.removeEventListener('pause', onPauseOrEnd);
      attached.removeEventListener('ended', onPauseOrEnd);
      attached.removeEventListener('seeking', onSeeking);
      attached.removeEventListener('seeked', onSeeked);
      attached.removeEventListener('timeupdate', onTimeUpdate);
      attached = null;
    };

    const attach = () => {
      const video = window.Video as HTMLMediaElement | null | undefined;
      if (!video || video === attached) return !!video;
      detach();
      attached = video;
      video.addEventListener('play', onPlay);
      video.addEventListener('playing', onPlay);
      video.addEventListener('pause', onPauseOrEnd);
      video.addEventListener('ended', onPauseOrEnd);
      video.addEventListener('seeking', onSeeking);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('timeupdate', onTimeUpdate);
      // Catch already-playing media (late attach after load).
      if (!video.paused && !video.ended) {
        flushPlaybackHighlight(video.currentTime || 0, {
          isFollowing: isFollowingRef.current,
        });
        if (isEditModeRef.current) {
          if (!transcriptChunkStore.isDwcm()) {
            startPlaybackHighlightLoop({ isFollowing: false });
          }
        } else {
          startHighlightForVideo(video);
        }
      } else if (
        transcriptChunkStore.isDwcm() &&
        transcriptChunkStore.isEditorReady() &&
        ((video.currentTime || 0) <= 0.05 ||
          transcriptChunkStore.isBeforeFirstCaptionTime(video.currentTime || 0))
      ) {
        syncBootTranscriptPresentation(video.currentTime || 0);
      }
      return true;
    };

    attach();
    const poll = window.setInterval(() => {
      if (cancelled) return;
      if (attach() && attached) {
        // Keep a slow poll in case media element is replaced on layer switch.
      }
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      stopPlaybackHighlightLoop();
      detach();
    };
  }, [syncPlaybackWindowing, editor, startHighlightForVideo]);

  useEffect(() => {
    if (!editor) return;

    window.Editor = editor;
  }, [editor]);

  useEffect(() => {
    if (!interactionLocked) return;
    try {
      ReactEditor.deselect(editor);
      Transforms.deselect(editor);
    } catch {
      // ignore invalid selection during chunk/window swap
    }
    try {
      window.getSelection()?.removeAllRanges();
    } catch {
      // ignore
    }
  }, [editor, interactionLocked]);

  const onChangeEdit = (e: any) => {
    const t0 = performance.now();
    try {
      // Selection-only ops must not push the full document into React state (huge re-render).
      const isAstChange = editor.operations.some(
        (op: any) => op.type !== 'set_selection',
      );

      if (isAstChange && !isNil(editor?.history)) {
        const canUndo = !isEmpty(
          editor.history.undos.filter(
            (i: any) => i?.[0]?.type !== 'set_selection',
          ),
        );
        const canRedo = !isEmpty(
          editor.history.redos.filter(
            (i: any) => i?.[0]?.type !== 'set_selection',
          ),
        );

        if (transcriptCanUndoRef.current !== canUndo) {
          dispatch(setCanUndo(canUndo));
        }

        if (transcriptCanRedoRef.current !== canRedo) {
          dispatch(setCanRedo(canRedo));
        }
      }

      // Pin caret caption only when the block changes (not every keystroke)
      if (toggle.isEditMode && editor.selection) {
        try {
          const blockPath = editor.selection.anchor.path.slice(0, 1);
          const block: any = Node.get(editor, blockPath);
          const key = transcriptVisibilityKey(block?.data?.start);
          if (key && key !== lastSelectionPinRef.current) {
            lastSelectionPinRef.current = key;
            transcriptVisibility.pin(key, 'selection');
          }
        } catch {
          // ignore invalid selection
        }
      }

      if (isAstChange && toggle.isEditMode) {
        const tSetDoc = performance.now();
        setDocument(e as CustomElement[]);
        if (shouldLogTypingPerf()) {
          const setDocMs = performance.now() - tSetDoc;
          if (setDocMs > TYPING_SLOW_LOG_MS) {
            console.log(
              `[typing-perf] setDocument slow ${setDocMs.toFixed(2)}ms`,
            );
          }
        }
      }

      const elapsed = performance.now() - t0;
      if (shouldLogTypingPerf() && elapsed > TYPING_SLOW_LOG_MS) {
        console.log(
          `[typing-perf] onChangeEdit slow ${elapsed.toFixed(
            2,
          )}ms ops=${editor.operations.length} astChange=${isAstChange}`,
        );
      }
    } catch (error) {
      console.error('[typing-perf] onChangeEdit error', error);
      throw error;
    }
  };

  // search text
  const search = useSelector(
    (state: RootState) => state.player.finderOptions.textSearch,
  );

  const finderOptions = useSelector(
    (state: RootState) => state.player.finderOptions,
  );
  const presentOccId = finderOptions.presentOccId;

  const isChunkedFinder =
    transcriptChunkStore.isEnabled() &&
    transcriptChunkStore.getIndex().length > 1;

  /** Row → matches; avoids returning all searchRanges from every caption decorate. */
  const searchRangesByRow = useMemo(() => {
    // SPP-655: finder paths are global; remap into active chunk local rows for decorate.
    if (isChunkedFinder) {
      return transcriptChunkStore.remapGlobalRangePathsToLocal(
        finderOptions?.searchRanges,
      );
    }
    return indexSearchRangesByRow(finderOptions?.searchRanges);
  }, [finderOptions?.searchRanges, finderLayoutKey, isChunkedFinder]);

  // After find jump / chunk swap — force Slate to re-run decorate with remapped ranges.
  useEffect(() => {
    if (!search || !finderOptions?.searchRanges?.length) return;
    const raf = requestAnimationFrame(() => {
      try {
        const editor = window.Editor;
        if (!editor) return;
        if (editor.selection) {
          Transforms.select(editor, editor.selection);
        }
      } catch {
        // ignore invalid selection during chunk swap
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [finderLayoutKey, finderOptions?.searchRanges, search]);

  const selectedTagId = useSelector(
    (state: RootState) => state.player.selectedTagId,
  );
  const keywords = useSelector((state: RootState) => state.player.keywords);
  const keywordLatest = useLatest(keywords);
  const entities = useSelector((state: RootState) => state.player.entities);
  const player = useSelector((state: RootState) => state.player);

  const layers = useSelector((state: RootState) => state.layers);
  const [keyWordArray, setKeywordArray] = useState<IKeyItem[] | undefined>(
    keywordLatest.current,
  );

  /** Layer keyword occurrences keyed by word start — O(1) per leaf decorate. */
  const keywordOccByStart = useMemo(() => {
    const starts = new Set<number>();
    if (selectedTagId === null || selectedTagId === -1) return starts;
    const occs = keyWordArray?.[selectedTagId]?.mentions?.[0]?.occurrences;
    if (!occs?.length) return starts;
    for (let i = 0; i < occs.length; i++) {
      const s = occs[i]?.s;
      if (typeof s === 'number') starts.add(s);
    }
    return starts;
  }, [selectedTagId, keyWordArray]);

  useEffect(() => {
    if (layers.currentLayer === Layers.CUSTOM_TERM) {
      const customTerms =
        player.layerCustomTerms?.find(
          (t) => t?.listId === player?.focusLayerCustomTermId,
        )?.matched ?? [];
      setKeywordArray(customTerms);
    }
    if (layers.currentLayer === Layers.PEOPLE) {
      setKeywordArray(entities.person);
    }
    if (layers.currentLayer === Layers.PRODUCT) {
      setKeywordArray(entities.product);
    }
    if (layers.currentLayer === Layers.LOCATION) {
      setKeywordArray(entities.location);
    }
    if (layers.currentLayer === Layers.NATIONALITY) {
      setKeywordArray(entities.nationality);
    }
    if (layers.currentLayer === Layers.ORG) {
      setKeywordArray(entities.organisation);
    }
    if (layers.currentLayer === Layers.KEY_TERM) {
      setKeywordArray(keywordLatest.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layers.currentLayer,
    keywordLatest.current,
    player.focusLayerCustomTermId,
  ]);

  const keywordRes = useRef<number>(0);
  const keywordResArray = useRef<any>([]);
  const rangeKeyword = useRef<any>([]);

  const textKeyword = useSelector(
    (state: RootState) => state.caption.textKeyword,
  );

  const lengthKeyword = useRef(0);
  const textKeywordChange = useRef('');
  const editCaption = useSelector(
    (state: RootState) => state.toggle.isEditCaption,
  );

  useEffect(() => {
    // edit keyword
    if (rangeKeyword.current.length && textKeyword && editCaption) {
      const oldText = keywords[selectedTagId!].keyword;
      const arrayFilter = uniqBy(rangeKeyword.current, JSON.stringify);

      arrayFilter.forEach((path: any) => {
        const edges = Editor.edges(window.Editor, [path[0]]);
        const newPath = {
          anchor: {
            path: path,
            offset: 0,
          },
          focus: {
            path: [path[0], path[1] + textKeyword.split(' ').length],
            offset: oldText.split(' ').slice(-1).length - 1,
          },
        };

        if (newPath.focus.path[1] > edges[1].path[1]) {
          newPath.focus.path[0] += 1;
          newPath.focus.path[1] = 0;
          newPath.focus.offset = textKeyword.length - 1;
        }
        const editor: any = window.Editor;
        let isLastWord = false;
        let newTextKeyword = `${textKeyword} `;
        const keywordEditLength = path[1] + textKeyword.split(' ').length;
        const lengthCaption = editor.children?.[path[0]]?.children.length;

        if (keywordEditLength < lengthCaption) {
          // can fully in one line
          if (
            editor.children?.[path[0]]?.children?.[
              keywordEditLength
            ].text.includes('.', ',', '?', '!')
          ) {
            isLastWord = true;
          }
        }
        if (keywordEditLength === lengthCaption - 1) {
          // last word in line
          isLastWord = true;
        }
        if (isLastWord) {
          newTextKeyword = `${textKeyword}`;
        }
        if (ReactEditor.hasRange(window.Editor, newPath)) {
          Transforms.insertText(editor, newTextKeyword, {
            at: newPath,
          });
        }
      });
      rangeKeyword.current = [];
      dispatch(toggleEditCaption(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textKeyword, editCaption]);

  const currentId = useRef<any>(-1);

  const decorate = useCallback(
    ([node, path]) => {
      // Finder: leaf-level only — clip ranges so "hat" in "that" highlights "hat".
      if (search && path?.length === 2 && node.text != null) {
        const hits = searchRangesByRow.get(path[0]);
        const textLength = node.text.length;
        return (
          hits
            ?.map(({ range, globalIndex }) => {
              const clipped = clipSearchRangeToLeaf(range, path, textLength);
              if (!clipped) return null;
              return {
                ...clipped,
                focusHighlight:
                  presentOccId > 0 && globalIndex === presentOccId - 1,
              };
            })
            .filter(Boolean) ?? []
        );
      }

      // Typing hot path: skip keyword decorate while editing unless
      // a keyword/tag selection is active (layer click-to-highlight).
      if (
        toggle.isEditMode &&
        (selectedTagId === null || selectedTagId === -1)
      ) {
        return [];
      }

      if (currentId.current !== selectedTagId) {
        currentId.current = selectedTagId;
        rangeKeyword.current = [];
      }
      if (keywordRes.current) {
        keywordRes.current -= 1;
        return [keywordResArray.current.shift()];
      }
      let ranges: any = [];

      // Playback word/caption highlight is DOM-driven (playbackHighlight.ts).
      // Do not decorate isCurrent here — that re-ran on every timeupdate and froze large files.

      // Layer keyword click-to-highlight (O(1) lookup by word start)
      if (
        selectedTagId !== null &&
        selectedTagId !== -1 &&
        node.text &&
        typeof node?.data?.dataStart === 'number' &&
        keywordOccByStart.has(node.data.dataStart)
      ) {
        textKeywordChange.current = node?.text;
        const keywordLength =
          keyWordArray?.[selectedTagId]?.keyword?.split(' ');
        if (!keywordLength?.length) return [];
        keywordRes.current = keywordLength.length - 1;
        lengthKeyword.current = keywordLength.length;
        keywordLength.forEach((value, index) => {
          if (index !== 0) {
            keywordResArray.current.push({
              focus: { path, offset: 0 },
              anchor: {
                path: [path[0], path[1] + index],
                offset: value.length + 1,
              },
              isKeyword: true,
            });
          } else {
            ranges = [
              {
                focus: { path, offset: 0 },
                anchor: {
                  path: [path[0], path[1] + 1],
                  offset: keywordLength[0].length + 1,
                },
                isKeyword: true,
              },
            ];
            rangeKeyword.current.push(path);
          }
        });
        return ranges;
      }

      return ranges;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      search,
      toggle.isEditMode,
      selectedTagId,
      presentOccId,
      finderOptions?.searchRanges,
      searchRangesByRow,
      keyWordArray,
      keywordOccByStart,
    ],
  );

  const handleChangeTimecode = async (element: any) => {
    const path = ReactEditor.findPath(editor, element);
    const result = await timecodeRef.current?.show({
      path,
      start: element?.data?.start,
      end: element?.data?.end,
    });

    if (!result) return;

    // Perform update node
    const op: SetNodeOperation = {
      type: 'set_node',
      path,
      properties: {},
      newProperties: {
        data: { ...element.data, start: result?.start, end: result?.end },
      } as Partial<Node>,
    };
    editor.apply(op);
  };

  const newTimeCode = useSelector(
    (state: RootState) => state.chapter.newTimeCode,
  );
  const isParagraphMode = useSelector(
    (state: RootState) => state.player.isParagraphMode,
  );
  const isSplitCaption = useSelector(
    (state: RootState) => state.chapter.isSplitCaption,
  );
  const isSplitParagraph = useSelector(
    (state: RootState) => state.chapter.isSplitParagraph,
  );

  useEffect(() => {
    if (
      (!isParagraphMode && isSplitCaption) ||
      (isParagraphMode && isSplitParagraph)
    ) {
      const editor = window?.Editor;
      const parentNode: any = editor.children.find((child: any) => {
        return child?.data?.end >= newTimeCode;
      });
      if (parentNode) {
        try {
          const anchorPath = [parentNode.id - 1];
          parentNode.children.find((child: any, index: number) => {
            if (child.data?.dataStart >= newTimeCode) {
              anchorPath.push(index);
              return child;
            }
            return false;
          });

          if (anchorPath[0] === 0) return;

          Transforms.select(editor, anchorPath);
          // Break chapter
          breakCaption([anchorPath[0]], true, 'split:chapter');
        } catch (error: any) {
          console.log('Split Transcript Error :>> ', error);
        }
      }
      if (!isParagraphMode && isSplitCaption) {
        dispatch(setSplitCaption(false));
      }
      if (isParagraphMode && isSplitParagraph) {
        dispatch(setSplitParagraph(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParagraphMode, isSplitCaption, isSplitParagraph]);

  // SPLIT NODE ON ENTER
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Select current node only on CTRL + A
    if (e.key === 'a' && e.ctrlKey) {
      e.preventDefault();
      const sel: any = window.Editor.selection;
      const parentNode = Editor.parent(editor, Editor.path(editor, sel));
      Transforms.select(window.Editor, parentNode[1]);
      return;
    }

    // SPP-655: caret leaving chunk edges → activate neighbor chunk
    if (
      (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
      transcriptChunkStore.isEnabled() &&
      transcriptChunkStore.getIndex().length > 1 &&
      editor.selection
    ) {
      try {
        const row = editor.selection.anchor.path[0];
        const meta = transcriptChunkStore.getActiveMeta();
        const index = transcriptChunkStore.getIndex();
        if (meta) {
          if (e.key === 'ArrowUp' && row === 0 && meta.ordinal > 0) {
            e.preventDefault();
            if (window?.Editor?.children) {
              transcriptChunkStore.flushActiveFromEditor(
                window.Editor.children as any[],
              );
            }
            transcriptChunkStore.setActiveWithNeighbor(
              index[meta.ordinal - 1].chunkId,
            );
            return;
          }
          if (
            e.key === 'ArrowDown' &&
            row >= (editor.children?.length ?? 1) - 1 &&
            meta.ordinal < index.length - 1
          ) {
            e.preventDefault();
            if (window?.Editor?.children) {
              transcriptChunkStore.flushActiveFromEditor(
                window.Editor.children as any[],
              );
            }
            transcriptChunkStore.setActiveWithNeighbor(
              index[meta.ordinal + 1].chunkId,
            );
            return;
          }
        }
      } catch {
        // ignore invalid selection
      }
    }

    // Insert line break
    if (e.key === Keys.ENTER && e.altKey) {
      e.preventDefault();
      const editor = window?.Editor;
      editor.insertText('\n');
      return;
    }

    if (e.key === Keys.ENTER || e.key === Keys.ENTER_NUMPAD) {
      e.preventDefault();
      const editor = window?.Editor;
      const sel: any = window.Editor.selection;
      // Get current Transcript NODE by cursor position
      const parentNode = Editor.parent(editor, Editor.path(editor, sel));
      if (!sel || !editor || !parentNode) return;

      // Break to TWO NODE
      breakCaption(parentNode[1], false, 'split:enter');
      ReactEditor.deselect(window.Editor);
    }
  };

  const clickTranscript = () => {
    if (!isPublicShared()) return;

    try {
      const slateSelection = getSlateSelection();

      if (!isNil(slateSelection?.start)) {
        window.Video.currentTime = slateSelection.start + 0.001;
      }
    } catch (err: any) {
      // Not valid click point
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (
      isArray(e?.clipboardData?.types) &&
      e.clipboardData.types.includes('text/plain')
    ) {
      const textPlain = e.clipboardData.getData('text/plain');

      window?.Editor?.insertText(textPlain);
      e.preventDefault();
    }
  };

  return (
    <div css={[commonStyles, tw`relative`]}>
      <Slate editor={editor} value={document} onChange={onChangeEdit}>
        <Editable
          decorate={decorate}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          readOnly={!toggle.isEditMode || interactionLocked}
          onKeyDown={handleKeyDown}
          // spellCheck={true}
          onMouseUp={clickTranscript}
          onPaste={handlePaste}
        />
      </Slate>
      {interactionLocked && (
        <div
          aria-busy="true"
          aria-label="Loading transcript"
          css={tw`absolute inset-0 z-10 cursor-wait`}
          style={{ pointerEvents: 'auto' }}
        />
      )}

      <TimecodeModal ref={timecodeRef} />
    </div>
  );
};

const Element = (props: any) => {
  const { element, handleChangeTimecode } = props;

  switch (element?.type) {
    case 'caption':
    case 'paragraph':
      return (
        <Transcript
          attributes={props.attributes}
          element={props.element}
          children={props.children}
          onChangeTimecode={handleChangeTimecode}
        />
      );

    default:
      // For the default case, we delegate to Slate's default rendering.
      return <DefaultElement {...props} />;
  }
};

const Leaf = (props: any) => {
  const { attributes, children, leaf } = props;

  return (
    <span
      {...attributes}
      className="word"
      data-start={`${leaf.data?.dataStart}`}
      data-end={`${leaf.data?.dataEnd}`}
      css={[
        leaf.highlight && tw`bg-green-200`,
        leaf?.focusHighlight && tw`background-color[#66ee96]!`,
        leaf.isKeyword && tw`bg-sonnant-keyword`,
        leaf.isCurrent &&
          tw`text-sonnant-purple-2 text-shadow[0 0 1px #5551FF]!`,
      ]}
      // title={`${leaf.text} | ${leaf?.data?.dataStart} - ${leaf?.data?.dataEnd}`}
    >
      {children}
    </span>
  );
};
